import { Component, Inject, OnInit, ViewEncapsulation } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { RelapseService } from '@app/tvf/relapse/services/relapse.service';
import { ToastrService } from 'ngx-toastr';
import Utils from '@app/shared/utils/utils';

export function cpfCnpjValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const val = control.value;
    if (!val) return null; // let Validators.required handle empty values
    
    const clean = String(val).replace(/[\.\-\/\s]/g, '');

    if (clean.length === 11) {
      const isValid = /^\d{11}$/.test(clean);
      return isValid ? null : { invalidCpfCnpj: true };
    }

    if (clean.length === 14) {
      const isValid = /^[a-zA-Z0-9]{12}\d{2}$/.test(clean);
      return isValid ? null : { invalidCpfCnpj: true };
    }

    return { invalidCpfCnpj: true };
  };
}

@Component({
  selector: 'app-dialog-laudo-b2c',
  templateUrl: './dialog-laudo-b2c.component.html',
  styleUrls: ['./dialog-laudo-b2c.component.sass'],
  encapsulation: ViewEncapsulation.None
})
export class DialogLaudoB2cComponent implements OnInit {
  constructor(
    private fb: FormBuilder,
    private dialogRef: MatDialogRef<DialogLaudoB2cComponent>,
    private relapseService: RelapseService,
    private toastr: ToastrService,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) {}

  reportForm!: FormGroup;
  trades: any[] = [];
  trade: any = {};
  currentIndex = 1;
  reportCount = 0;
  isSubmitting = false;
  isLoadingTrade = true;

  get progressPercent(): string {
    if (this.reportCount === 0) return '0%';
    return `${(this.currentIndex / this.reportCount) * 100}%`;
  }

  ngOnInit(): void {
    const initialTrades = this.data?.trades || [];
    this.reportCount = initialTrades.length;
    this.currentIndex = 1;

    const tradesId = initialTrades.map((t: any) => t.tradeId);
    this.relapseService.getLaudoB2C(tradesId).subscribe({
      next: (res) => {
        this.trades = res?.data || res || [];
        this.reportCount = this.trades.length;
        this.isLoadingTrade = false;
        this.setCurrentTrade();
      },
      error: (err) => {
        console.error('B2C laudo error:', err);
        this.isLoadingTrade = false;
      },
    });

    this.reportForm = this.fb.group({
      dataRecebimento: [null, Validators.required],
      codigoRastreio: ['', [Validators.required, Validators.maxLength(200)]],
      cliente: ['', [Validators.required, Validators.maxLength(200)]],
      cpfCnpj: ['', [Validators.required, cpfCnpjValidator()]],
      motivoContestacao: ['', [Validators.required, Validators.maxLength(500)]],
    });
  }

  setCurrentTrade(): void {
    const selected = this.trades[this.currentIndex - 1];
    this.trade = {
      sku: selected?.sku || '',
      product: selected?.productDescription || '',
      idTrade: selected?.tradeId || '',
      imei: selected?.imei || '',
      assessmentDate: selected?.date || '',
      classification: selected?.classification || '',
      defects: selected?.customerDefects || '',
    };
  }

  onClienteInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const cleaned = input.value.replace(/[^a-zA-Z\u00C0-\u00FF ]/g, '');
    const titled = cleaned.split(' ').map(w => w.length ? w[0].toUpperCase() + w.slice(1) : '').join(' ');
    input.value = titled;
    this.reportForm.get('cliente')?.setValue(titled);
  }

  onCodigoRastreioInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const cleaned = input.value.replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
    input.value = cleaned;
    this.reportForm.get('codigoRastreio')?.setValue(cleaned);
  }

  onClose(): void {
    this.dialogRef.close();
  }

  cancel(): void {
    this.dialogRef.close();
  }

  submit(): void {
    if (this.reportForm.invalid || this.isSubmitting) return;

    this.isSubmitting = true;

    const dateVal = this.reportForm.get('dataRecebimento')?.value;
    let isoDate = '';
    if (dateVal) {
      if (dateVal instanceof Date) {
        isoDate = dateVal.toISOString();
      } else {
        const parsed = new Date(dateVal);
        if (!isNaN(parsed.getTime())) {
          isoDate = parsed.toISOString();
        }
      }
    }

    const payload = {
      TradeId: this.trade.idTrade,
      dataRecebimento: isoDate,
      motivoConstatacao: this.reportForm.get('motivoContestacao')?.value || '',
      codigoRastreio: this.reportForm.get('codigoRastreio')?.value || '',
      cliente: this.reportForm.get('cliente')?.value || '',
      cpfCnpj: this.reportForm.get('cpfCnpj')?.value || '',
    };

    this.relapseService.sendLaudoB2C(payload).subscribe({
      next: (res: Blob) => {
        this.isSubmitting = false;
        this.toastr.success(`Laudo B2C do Trade ${this.trade.idTrade} emitido com sucesso!`);
        if (res) {
          Utils.createAndDownloadBlobFile(res, `Laudo_B2C_${this.trade.idTrade}`, 'pdf');
        }

        if (this.currentIndex < this.reportCount) {
          this.reportForm.patchValue({
            dataRecebimento: null,
            codigoRastreio: '',
            cliente: '',
            cpfCnpj: '',
            motivoContestacao: ''
          });

          Object.keys(this.reportForm.controls).forEach(key => {
            const control = this.reportForm.get(key);
            control?.markAsPristine();
            control?.markAsUntouched();
            control?.updateValueAndValidity();
          });

          this.currentIndex++;
          this.setCurrentTrade();
        } else {
          this.dialogRef.close(true);
        }
      },
      error: (err) => {
        this.isSubmitting = false;
        this.toastr.error('Erro ao emitir laudo B2C. Tente novamente.');
        console.error(err);
      }
    });
  }
}
